import { useEffect, useState, ReactNode } from "react";

// react-router-dom components
import { useLocation, NavLink, useNavigate } from "react-router-dom";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React TS examples components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavList from "examples/Sidenav/SidenavList";
import SidenavItem from "examples/Sidenav/SidenavItem";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setUser,
  setIsAuthencated,
} from "context";
import MDAvatar from "components/MDAvatar";
import { keyLocalStorage } from "helpers/constants";
import { IconButton } from "@mui/material";
import { siteConfig } from "types/siteConfig";

// Declaring props types for Sidenav
interface Props {
  color?:
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "dark";
  brand?: string;
  brandName: string;
  routes: {
    [key: string]:
      | ReactNode
      | string
      | {
          [key: string]:
            | ReactNode
            | string
            | {
                [key: string]: ReactNode | string;
              }[];
        }[];
  }[];
  [key: string]: any;
}

function Sidenav({
  color,
  brand,
  brandName,
  routes,
  ...rest
}: Props): JSX.Element {
  const navigate = useNavigate();
  const { url } = siteConfig;
  const [openCollapse, setOpenCollapse] = useState<boolean | string>("");
  const [openNestedCollapse, setOpenNestedCollapse] = useState<
    boolean | string
  >("");
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, user } =
    controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const items = pathname.split("/").slice(1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];
  let textColor:
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "dark"
    | "white"
    | "inherit"
    | "text"
    | "light" = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // Encontrar qual menu deve estar aberto baseado nas rotas
    let menuKey = collapseName || "";

    // Se o collapseName não corresponde a nenhum menu diretamente,
    // verificar se alguma rota filha corresponde ao pathname
    if (!routes.find((r: any) => r.key === menuKey && r.type === "collapse")) {
      // Buscar em todas as rotas qual tem uma rota filha que corresponde ao pathname
      for (const route of routes) {
        if (
          route.type === "collapse" &&
          route.collapse &&
          Array.isArray(route.collapse)
        ) {
          const hasMatchingRoute = route.collapse.some((item: any) => {
            if (!item.route) return false;
            // Normalizar rotas: adicionar barra inicial se não tiver e comparar
            const normalizedItemRoute = item.route.startsWith("/")
              ? item.route
              : `/${item.route}`;
            const normalizedPathname = pathname || "";
            return normalizedItemRoute === normalizedPathname;
          });
          if (hasMatchingRoute && typeof route.key === "string") {
            menuKey = route.key;
            break;
          }
        }
      }
    }

    setOpenCollapse(menuKey);
    setOpenNestedCollapse(itemParentName || "");
  }, [collapseName, itemParentName, pathname, routes]);

  useEffect(() => {
    /** Em telas estreitas força menu compacto e tipo "escuro"; em desktop não altera transparente/branco (evita reset a cada navegação por closure obsoleta). */
    function handleMiniSidenav() {
      const narrow = window.innerWidth < 1200;
      setMiniSidenav(dispatch, narrow);
      if (narrow) {
        setTransparentSidenav(dispatch, false);
        setWhiteSidenav(dispatch, false);
      }
    }
    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch]);

  //Logout ao fechar todas as janelas.
  // useEffect(() => {
  //   const handleWindowClose = () => {
  //     handlerLogout();
  //   };

  //   window.addEventListener("beforeunload", handleWindowClose);

  //   return () => {
  //     window.removeEventListener("beforeunload", handleWindowClose);
  //   };
  // }, [dispatch]);

  //Logout com a tela parada mais de 5 minutos
  // useEffect(() => {
  //   checkIdleTime();
  // }, []);

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse: any) => {
    const template = collapse.map(({ name, route, key, href }: any) =>
      href ? (
        <Link
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavItem name={name} nested />
        </Link>
      ) : (
        <NavLink to={route} key={key} style={{ textDecoration: "none" }}>
          <SidenavItem name={name} active={route === pathname} nested />
        </NavLink>
      )
    );

    return template;
  };
  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses: any) =>
    collapses.map(({ name, collapse, route, href, key }: any) => {
      let returnValue;

      if (collapse) {
        returnValue = (
          <SidenavItem
            key={key}
            color={color}
            name={name}
            active={key === itemParentName ? "isParent" : false}
            open={openNestedCollapse === key}
            onClick={({ currentTarget }: any) =>
              openNestedCollapse === key &&
              currentTarget.classList.contains("MuiListItem-root")
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(key)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem color={color} name={name} active={key === itemName} />
          </Link>
        ) : (
          <NavLink to={route} key={key} style={{ textDecoration: "none" }}>
            <SidenavItem color={color} name={name} active={key === itemName} />
          </NavLink>
        );
      }
      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    ({
      type,
      name,
      icon,
      title,
      collapse,
      noCollapse,
      key,
      href,
      route,
    }: any) => {
      let returnValue;

      if (type === "collapse") {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                active={key === collapseName}
                noCollapse={noCollapse}
              />
            </Link>
          );
        } else if (noCollapse && route) {
          returnValue = (
            <NavLink to={route} key={key}>
              <SidenavCollapse
                name={name}
                icon={icon}
                noCollapse={noCollapse}
                active={key === collapseName}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </NavLink>
          );
        } else {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              open={openCollapse === key}
              onClick={() =>
                openCollapse === key
                  ? setOpenCollapse(false)
                  : setOpenCollapse(key)
              }
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }

      return returnValue;
    }
  );

  const handlerLogout = async () => {
    setUser(dispatch, null);
    setIsAuthencated(dispatch, false);
    localStorage.removeItem(keyLocalStorage.USER_STORAGE);
    localStorage.removeItem(keyLocalStorage.DEFAULT_CONSTANTS);
    navigate({ pathname: "/authentication/sign-in/basic" });
  };

  //Logout ao ficar com a tela parada a mais de 5 minutos
  // const checkIdleTime = () => {
  //   let idleTime = 0;
  //   const idleInterval = setInterval(() => {
  //     idleTime += 1;
  //     if (idleTime >= 900) {
  //       // 300 segundos = 5 minutos
  //       handlerLogout();
  //       clearInterval(idleInterval);
  //     }
  //   }, 1000);

  //   window.onmousemove = () => {
  //     idleTime = 0;
  //   };
  //   window.onkeypress = () => {
  //     idleTime = 0;
  //   };
  //   window.ontouchstart = () => {
  //     idleTime = 0;
  //   };
  // };

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox
          component={NavLink}
          to={url}
          target="_blank"
          display="flex"
          alignItems="center"
        >
          {brand && (
            <MDBox
              component="img"
              src={brand}
              alt="Brand"
              width={miniSidenav ? "2.2rem" : "4.2rem"}
            />
          )}
          <MDBox
            width={!brandName && "100%"}
            sx={(theme: any) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography
              component="h6"
              variant="button"
              fontWeight="medium"
              color={textColor}
            >
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox
          sx={{ cursor: "pointer" }}
          display="flex"
          alignItems="center"
          justifyContent={miniSidenav ? "center" : "space-between"}
        >
          <MDTypography
            display="flex"
            alignItems="center"
            color="white"
            gap={2}
            variant="subtitle2"
          >
            {user && (
              <MDAvatar
                src={user?.avatar}
                alt={user?.nome}
                size="sm"
                bgColor="dark"
              />
            )}
            {!miniSidenav && (
              <MDTypography
                variant="button"
                fontWeight="medium"
                color={textColor}
              >
                {user && user.nome.split(" ")[0]}
              </MDTypography>
            )}
          </MDTypography>
          {!miniSidenav && (
            <IconButton onClick={handlerLogout}>
              <Icon fontSize="medium" color="warning">
                logout
              </Icon>
            </IconButton>
          )}
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

// Declaring default props for Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

export default Sidenav;
